import { useCallback, useEffect, useRef, useState } from "react";
import "./styles/tokens.css";
import "./styles/layout.css";
import "./styles/controls.css";
import { RevealAnswersButton } from "./components/ClassroomTools";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ExhibitHeader } from "./components/ExhibitHeader";
import { FullscreenToggle } from "./components/FullscreenToggle";
import { InformationPanel } from "./components/InformationPanel";
import { PhaseSelector } from "./components/PhaseSelector";
import { PlaybackControls } from "./components/PlaybackControls";
import { StatusNotice } from "./components/StatusNotice";
import { Timeline } from "./components/Timeline";
import { ViewControls } from "./components/ViewControls";
import { readHashNumber, writeHashParams } from "./demos/shared/hashState";
import { FallbackExhibit } from "./fallback/FallbackExhibit";
import { SceneCanvas } from "./scene/SceneCanvas";
import type { SceneQuality } from "./scene/SceneCanvas";
import { TOUR_STEPS } from "./scene/tour";
import {
  SimulationProvider,
  useSimulation,
} from "./simulation/SimulationProvider";

function localFallbackRequested() {
  // Deterministic local test switch only: read once, never persist or transmit it.
  return (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("fallback") === "1"
  );
}

function SimulationApp({ forceFallback }: { readonly forceFallback: boolean }) {
  const [fallbackActive, setFallbackActive] = useState(
    () => forceFallback || localFallbackRequested(),
  );
  const [sceneQuality, setSceneQuality] = useState<SceneQuality | null>(null);
  const [tourStep, setTourStep] = useState<number | null>(null);
  const [readoutVisible, setReadoutVisible] = useState(false);
  const [predictMode, setPredictMode] = useState(false);
  const [answersRevealed, setAnswersRevealed] = useState(false);
  const layoutRef = useRef<HTMLElement | null>(null);
  const { resetView, state } = useSimulation();

  // Keep the URL shareable whenever the cycle is paused or scrubbed.
  useEffect(() => {
    if (state.isPlaying || state.transition !== null) {
      return;
    }
    writeHashParams({ day: state.day.toFixed(1) });
  }, [state.day, state.isPlaying, state.transition]);
  const activateFallback = useCallback(() => {
    setSceneQuality(null);
    setTourStep(null);
    setFallbackActive(true);
  }, []);
  const updateSceneQuality = useCallback((quality: SceneQuality) => {
    setSceneQuality((currentQuality) =>
      currentQuality === quality ? currentQuality : quality,
    );
  }, []);
  const endTour = useCallback(() => {
    setTourStep(null);
    resetView();
  }, [resetView]);
  const advanceTour = useCallback(() => {
    setTourStep((step) => {
      if (step === null) {
        return step;
      }
      return step + 1 < TOUR_STEPS.length ? step + 1 : null;
    });
  }, []);
  const tourCaption = tourStep === null ? null : TOUR_STEPS[tourStep]?.caption;
  const isLastTourStep = tourStep === TOUR_STEPS.length - 1;

  const finishTourIfDone = useCallback(() => {
    if (isLastTourStep) {
      endTour();
    } else {
      advanceTour();
    }
  }, [advanceTour, endTour, isLastTourStep]);

  return (
    <main className="app-shell">
      <ExhibitHeader
        readoutVisible={readoutVisible}
        onToggleReadout={() => setReadoutVisible((visible) => !visible)}
      />
      <section
        aria-label="Moon phase simulation"
        className={`exhibit-layout${readoutVisible ? "" : " readout-hidden"}`}
        ref={layoutRef}
      >
        <section className="scene-region" aria-label="Orbital geometry view">
          <StatusNotice quality={fallbackActive ? null : sceneQuality} />
          <div className="scene-slot" data-testid="scene-slot">
            <FullscreenToggle targetRef={layoutRef} />
            {fallbackActive ? (
              <FallbackExhibit />
            ) : (
              <ErrorBoundary onUseFallback={activateFallback}>
                <SceneCanvas
                  onFallbackNeeded={activateFallback}
                  onQualityChange={updateSceneQuality}
                  tourStep={tourStep}
                />
                <div
                  aria-label="Earth observer Moon view"
                  className="earth-view-slot"
                  data-testid="earth-view-slot"
                  role="img"
                >
                  <span aria-hidden="true" className="earth-view-north">
                    N
                  </span>
                </div>
                {tourStep === null ? (
                  <button
                    className="tour-launch"
                    type="button"
                    onClick={() => setTourStep(0)}
                  >
                    Tutorial
                  </button>
                ) : (
                  <div
                    aria-label="Guided setup tour"
                    className="tour-overlay"
                    role="group"
                  >
                    <p aria-live="polite" className="tour-caption">
                      {tourCaption}
                    </p>
                    <div className="tour-actions">
                      <button type="button" onClick={finishTourIfDone}>
                        {isLastTourStep ? "Done" : "Next"}
                      </button>
                      {isLastTourStep ? null : (
                        <button type="button" onClick={endTour}>
                          Skip tour
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </ErrorBoundary>
            )}
          </div>
          {forceFallback ? null : (
            <p className="fallback-switch">
              <button
                type="button"
                onClick={() =>
                  fallbackActive ? setFallbackActive(false) : activateFallback()
                }
              >
                {fallbackActive
                  ? "Try the full 3D view"
                  : "Use the simple 2D view"}
              </button>
              {fallbackActive ? null : (
                <span> — for older devices or to save data.</span>
              )}
            </p>
          )}
          <p className="orientation-note">
            North-up geometry view; horizon orientation varies by location and
            time.
          </p>
        </section>
        {readoutVisible ? (
          <aside
            aria-label="Notes"
            className={`teaching-panel${
              predictMode && !answersRevealed ? " predict-mode" : ""
            }`}
          >
            {predictMode ? (
              <RevealAnswersButton
                revealed={answersRevealed}
                onToggle={() => setAnswersRevealed((current) => !current)}
              />
            ) : null}
            <InformationPanel />
          </aside>
        ) : null}
        <section className="controls-panel" aria-label="Simulation controls">
          <PlaybackControls />
          <Timeline />
          <PhaseSelector />
          <ViewControls />
          <fieldset className="control-group classroom-tools">
            <legend>Classroom tools</legend>
            <label className="checkbox-control">
              <input
                checked={predictMode}
                type="checkbox"
                onChange={() => {
                  setPredictMode((current) => !current);
                  setAnswersRevealed(false);
                }}
              />
              Predict mode — hide answers
            </label>
          </fieldset>
        </section>
        <p className="scale-note">
          Educational scale — sizes and distances are compressed; angles and
          illumination geometry are preserved.
        </p>
      </section>
    </main>
  );
}

export function App({
  forceFallback = false,
}: {
  readonly forceFallback?: boolean;
}) {
  // Read the shared lesson link synchronously so it is part of the very
  // first render — see SimulationProvider.
  const [sharedDay] = useState(() => readHashNumber("day"));
  return (
    <SimulationProvider initialDay={sharedDay}>
      <SimulationApp forceFallback={forceFallback} />
    </SimulationProvider>
  );
}
